import { useEffect, useState } from "react";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Calendar, MapPin, ExternalLink, Ticket, User, Repeat, CalendarCheck } from "lucide-react";
import { fetchEventBySlug, EVENT_CATEGORY_LABELS, EVENT_TYPE_LABELS, RECURRENCE_LABELS } from "@/lib/events.functions";

import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { SmartImage } from "@/components/smart-image";
import { useTranslation } from "@/i18n";

const evOptions = (slug: string) =>
  queryOptions({
    queryKey: ["event", slug],
    queryFn: async () => {
      const ev = await fetchEventBySlug(slug);
      if (!ev || ev.status !== "published") throw notFound();
      return ev;
    },
  });

export const Route = createFileRoute("/events/$slug")({
  loader: ({ params, context }) => context.queryClient.ensureQueryData(evOptions(params.slug)),
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Event unavailable — BidNova Auctions" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const ev = loaderData;
    const url = `https://bidnovaauctions.uk/events/${params.slug}`;
    const desc =
      ev.description?.slice(0, 155) ??
      `${ev.name} — ${[ev.venue, ev.city, ev.country].filter(Boolean).join(", ")}.`;
    return {
      meta: [
        { title: `${ev.name} — BidNova Events` },
        { name: "description", content: desc },
        { property: "og:title", content: ev.name },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "event" },
        ...(ev.image_url
          ? [
              { property: "og:image", content: ev.image_url },
              { name: "twitter:image", content: ev.image_url },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Event",
            name: ev.name,
            description: ev.description ?? undefined,
            startDate: ev.start_time ? `${ev.start_date}T${ev.start_time}` : ev.start_date,
            endDate: ev.end_date
              ? ev.end_time
                ? `${ev.end_date}T${ev.end_time}`
                : ev.end_date
              : undefined,
            image: ev.image_url ?? undefined,
            url,
            eventStatus: "https://schema.org/EventScheduled",
            eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
            location: {
              "@type": "Place",
              name: ev.venue ?? ev.city ?? "Venue",
              address: {
                "@type": "PostalAddress",
                streetAddress: ev.address ?? undefined,
                addressLocality: ev.city ?? undefined,
                addressCountry: ev.country ?? undefined,
              },
            },
            organizer: ev.organiser
              ? { "@type": "Organization", name: ev.organiser, url: ev.website_url ?? undefined }
              : undefined,
            offers: ev.ticket_url
              ? { "@type": "Offer", url: ev.ticket_url, availability: "https://schema.org/InStock" }
              : undefined,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://bidnovaauctions.uk/" },
              { "@type": "ListItem", position: 2, name: "Events", item: "https://bidnovaauctions.uk/events" },
              { "@type": "ListItem", position: 3, name: ev.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: EventDetail,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

function useCountdown(target: Date | null) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [target]);
  if (!target) return null;
  const diff = target.getTime() - now;
  if (diff <= 0) return null;
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  return { days, hours, minutes };
}

function EventDetail() {
  const { t } = useTranslation("community");
  const { slug } = Route.useParams();
  const { data: ev } = useSuspenseQuery(evOptions(slug));
  const startISO = ev.start_time ? `${ev.start_date}T${ev.start_time}` : `${ev.start_date}T00:00:00`;
  const startDate = new Date(startISO);
  const countdown = useCountdown(Number.isNaN(startDate.getTime()) ? null : startDate);
  const dateLine = (() => {
    const start = new Date(ev.start_date + "T00:00:00");
    const sTxt = start.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    if (!ev.end_date || ev.end_date === ev.start_date) return sTxt;
    const e = new Date(ev.end_date + "T00:00:00");
    return `${sTxt} – ${e.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}`;
  })();
  return (
    <article className="mx-auto max-w-4xl px-6 sm:px-8 py-14">
      <nav aria-label="Breadcrumb" className="mb-6 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-primary">{t("layout.breadcrumbHome", "Home")}</Link>
        <span className="mx-2">/</span>
        <Link to="/events" className="hover:text-primary">{t("nav.events", "Events")}</Link>
        <span className="mx-2">/</span>
        <span className="text-white/80">{ev.name}</span>
      </nav>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-[0.2em] text-primary">
          {EVENT_CATEGORY_LABELS[ev.category] ?? ev.category}
        </span>
        {ev.event_type && ev.event_type !== ev.category && (
          <span className="rounded-full border border-white/15 bg-white/[0.04] px-2.5 py-0.5 text-[11px] uppercase tracking-widest text-muted-foreground">
            {EVENT_TYPE_LABELS[ev.event_type] ?? ev.event_type}
          </span>
        )}
        {ev.is_recurring && (
          <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 text-[11px] uppercase tracking-widest text-primary">
            <Repeat className="h-3 w-3" aria-hidden />
            {ev.recurrence_pattern ? RECURRENCE_LABELS[ev.recurrence_pattern] ?? t("events.detail.recurring", "Recurring") : t("events.detail.recurring", "Recurring")}
          </span>
        )}
      </div>
      <h1 className="mt-2 font-display text-4xl text-white">{ev.name}</h1>
      {ev.image_url && (
        <SmartImage
          src={ev.image_url}
          alt={`${ev.name} event`}
          width={1024}
          height={576}
          widths={[640, 960, 1280]}
          sizes="(max-width: 768px) 100vw, 768px"
          priority
          className="mt-8 w-full rounded-3xl border border-white/10 object-cover aspect-[16/9]"
        />
      )}
      {countdown && (
        <div
          role="timer"
          aria-live="polite"
          aria-label={`Event starts in ${countdown.days} days ${countdown.hours} hours ${countdown.minutes} minutes`}
          className="mt-6 grid grid-cols-3 gap-3 rounded-2xl border border-primary/25 bg-primary/[0.06] p-4 text-center"
        >
          {[
            { v: countdown.days, l: t("events.detail.days", "Days") },
            { v: countdown.hours, l: t("events.detail.hours", "Hours") },
            { v: countdown.minutes, l: t("events.detail.minutes", "Minutes") },
          ].map(({ v, l }) => (
            <div key={l}>
              <div className="font-display text-3xl text-white tabular-nums">{v}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-8 grid gap-6 sm:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
          <div className="flex items-center gap-2 text-primary">
            <Calendar className="h-4 w-4" /> <span className="text-xs uppercase tracking-widest">{t("events.detail.when", "When")}</span>
          </div>
          <p className="mt-2 text-white">{dateLine}</p>
          {(ev.start_time || ev.end_time) && (
            <p className="mt-1 text-sm text-muted-foreground">
              {ev.start_time ?? ""}{ev.end_time ? ` – ${ev.end_time}` : ""}
            </p>
          )}
        </div>
        {(ev.venue || ev.city) && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
            <div className="flex items-center gap-2 text-primary">
              <MapPin className="h-4 w-4" /> <span className="text-xs uppercase tracking-widest">{t("events.detail.where", "Where")}</span>
            </div>
            <p className="mt-2 text-white">{ev.venue}</p>
            <p className="text-sm text-muted-foreground">
              {[ev.address, ev.city, ev.country].filter(Boolean).join(", ")}
            </p>
            {ev.maps_url && (
              <a
                href={ev.maps_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                {t("events.detail.openInMaps", "Open in Maps")} <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        )}
      </div>
      {ev.description && (
        <div className="mt-8 whitespace-pre-wrap rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-white/90 leading-relaxed">
          {ev.description}
        </div>
      )}
      <div className="mt-8 flex flex-wrap gap-3">
        {ev.rsvp_url && (
          <a
            href={ev.rsvp_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-2xl bg-primary btn-lift h-11 px-5 text-sm font-medium text-primary-foreground"
          >
            <CalendarCheck className="h-4 w-4" /> {t("events.detail.rsvp", "RSVP")}
          </a>
        )}
        {ev.ticket_url && (
          <a
            href={ev.ticket_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-2xl bg-gold-gradient btn-lift h-11 px-5 text-sm font-medium text-gold-foreground"
          >
            <Ticket className="h-4 w-4" /> {t("events.detail.getTickets", "Get Tickets")}
          </a>
        )}
        {ev.website_url && (
          <a
            href={ev.website_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-2xl border border-white/15 h-11 px-5 text-sm text-white hover:border-primary/60"
          >
            <ExternalLink className="h-4 w-4" /> {t("events.detail.visitWebsite", "Visit Website")}
          </a>
        )}
      </div>
      {ev.organiser && (
        <p className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
          <User className="h-4 w-4" aria-hidden /> {t("events.detail.organisedBy", "Organised by {name}", { name: ev.organiser })}
        </p>
      )}
    </article>
  );
}

